import fhirDao from '@/helperScripts/fhirHelpers/dao/fhirDao';
import { useFilterStore } from '@/stores/filterStore';
import { useFhirStore } from '@/stores/fhirStore';
import valueSetHelper from '@/helperScripts/fhirHelpers/terminology/valueSetHelper';
import i18n from '@/i18n';
import { FilterCharactType } from '@/tsEnums/FilterCharactType';
import icdProcessor from '@/helperScripts/icdCodes/icdProcessor';
import type { IcdValueSetEntry } from '@/helperScripts/icdCodes/icdProcessor';

import { VariantInfo } from '@/tsClasses/VariantInfoForProteinExchange'; //Needed for type in jsdoc
import {
  CharacteristicFilterInfoObject,
  FilterInfoObjectInputVal,
} from '@/tsInterfaces/CharacteristicFilterInfoObject';
import { FilterParameters } from '@/tsInterfaces/FilterParameters';
import { LocationFilterInfo } from '@/tsInterfaces/LocationFilterInfo';
import { areVariantsEquivalent } from '../variantHelpers/proteinChangeComparator';
import { Coding } from 'fhir/r4';
import { useGeneralFlagStore } from '@/stores/generalFlagStore';

const { t } = i18n.global;

const readIndividualCharacteristicFiltersFromUrl = (
  querySearchParams: FilterParameters
) => {
  const filterStore = useFilterStore();
  const filterNamesInStore = Object.keys(
    filterStore.individualCharacteristicFilterArrays
  );

  //The following logic assumes that all individual filter arrays from the query are called the same as their counterparts in the filterStore. If this changes, the individual set operations have to be typed out separately.
  filterNamesInStore.forEach((filterName) => {
    setIndividualCharacteristicFilterInStore(
      filterName,
      filterName,
      querySearchParams
    );
  });
};
/**
 *
 * @param {string} nameOfFilterInUrl
 * @param {string} nameOfFilterInStore
 * @param {} querySearchParams
 * @returns
 */
const setIndividualCharacteristicFilterInStore = (
  nameOfFilterInUrl: string,
  nameOfFilterInStore: string,
  querySearchParams: FilterParameters
) => {
  const filterStore = useFilterStore();

  //Parses individual properties from query and sets them in the store
  if (!querySearchParams) {
    return;
  }

  if (querySearchParams[nameOfFilterInUrl]) {
    const paramArray = querySearchParams[nameOfFilterInUrl];
    filterStore.individualCharacteristicFilterArrays[nameOfFilterInStore] =
      paramArray;
  } else {
    filterStore.individualCharacteristicFilterArrays[nameOfFilterInStore] = [];
  }
};

// Common Methods for manipulating filters in store

/**
 * Set isParentCategoryFilterElement flag on given filter object. Identifies individual filters for categories like tumor region and tumor entity
 */
function updateParentCategoryList(newFilterInfo, originalFilterElement) {
  if (newFilterInfo) {
    originalFilterElement.inputVal = newFilterInfo.inputVal;

    originalFilterElement.isParentCategoryFilterElement =
      newFilterInfo.isParentCategoryFilterElement;

    originalFilterElement.charactType = newFilterInfo.charactType;

    originalFilterElement.autoCreatedByCategorySelection = false;

    originalFilterElement.categoryId = newFilterInfo.categoryId;
  }
}

function getEmptyCharacteristicFilterObjectAdvancedMode() {
  const objectTemplate = {
    inputVal: getEmptyCharacteristicFilterInputObject(),
    charactType: FilterCharactType.UNKNOWN,
    valueBoolean: undefined,
    categoryId: '',
    isParentCategoryFilterElement: false,
    autoCreatedByCategorySelection: false,
  };

  return { ...objectTemplate };
}

function getEmptyCharacteristicFilterInputObject() {
  return {
    code: '',
    display: '',
    system: '',
    icdSpecific: '',
    icdGeneral: '',
    exclusionCriterion: false,
  };
}

async function addFiltersFromCategorySimpleMode(
  arrayToAddFiltersTo,
  categoryFilterObject,
  category,
  nameOfFilterInStore
) {
  const filterStore = useFilterStore();

  //Get all diagnosis codings associated with selected category
  const diagnosisCodings = await fhirDao.getDiagnosesForCategory(
    categoryFilterObject,
    category
  );
  //Add codings to diagnosis filters

  //fetch corresponding categoryFilterObject from store since it is of type CharacteristicFilterInfoObject and has categoryId
  const categoryFilterObjectFromStore =
    filterStore.individualCharacteristicFilterArrays[nameOfFilterInStore].find(
      (item) => item.inputVal.code === categoryFilterObject.code
    );

  // Use a Set to keep track of unique codes (for performance reasons)
  const uniqueCodes = new Set(arrayToAddFiltersTo.map((elem) => elem.code));

  //Loop through diagnosisCodings array, check if coding is already present in filters, add it only if it isn't. Prevents duplicates.
  for (let i = 0; i < diagnosisCodings.length; i++) {
    const coding = diagnosisCodings[i];
    // Check if code is already present in filters using the Set
    if (!uniqueCodes.has(coding.code)) {
      // Add coding to filters and to the Set
      const newCharacteristicFilterInfoObject = {
        //of type CharacteristicFilterInfoObject
        charactType: FilterCharactType.CONDITION,
        inputVal: coding,
        isParentCategoryFilterElement: false,
        autoCreatedByCategorySelection: true,
        categoryId: categoryFilterObjectFromStore.categoryId,
      };
      arrayToAddFiltersTo.push(newCharacteristicFilterInfoObject);
      uniqueCodes.add(coding.code);
    }
  }
}

/**
 * Removes tumorentity or tumorregion filters if none of their corresponding icd codes remain as active filters.
 * @param filterArray Array to be scanned for obsolete entries (e.g. array with tumorEntity filters)
 * @param categoryName Either 'Tumorentität' or 'Tumorregion'
 */
async function removeObsoleteCategoryFiltersSimpleMode(
  filterArray,
  categoryName: string
) {
  const filterStore = useFilterStore();

  const filtersToRemove = new Array<CharacteristicFilterInfoObject>();

  if (filterArray && categoryName) {
    for (let i = 0; i < filterArray.length; i++) {
      const categoryFilter = filterArray[i];
      const icdCodes = await fhirDao.getDiagnosesForCategory(
        categoryFilter.inputVal,
        categoryName
      );
      let correspondingCodeFound = false;
      let c = 0;
      while (
        !correspondingCodeFound &&
        c <
          filterStore.individualCharacteristicFilterArrays.conditionFilters
            .length
      ) {
        const currentConditionFilter =
          filterStore.individualCharacteristicFilterArrays.conditionFilters[c];
        if (
          icdCodes.filter(
            (diagnosisInfo) =>
              'code' in currentConditionFilter.inputVal &&
              diagnosisInfo.code === currentConditionFilter.inputVal.code
          ).length > 0 &&
          currentConditionFilter.categoryId === categoryFilter.categoryId
        ) {
          correspondingCodeFound = true;
        } else {
          c++;
        }
      }
      if (!correspondingCodeFound) {
        //Filter is obsolete --> Mark for removal
        filtersToRemove.push(categoryFilter);
      }
    }

    //Remove obsolete elements
    filtersToRemove.forEach((elem) => {
      const idx = filterArray.indexOf(elem);
      if (idx !== -1) {
        //Guard against elements that were already removed (splice(-1, 1) would delete the last element)
        filterArray.splice(idx, 1);
      }
    });
  }
}

function addEmptyCharacteristicFilterInput() {
  const filterStore = useFilterStore();

  filterStore.characteristicFilters.push(
    getEmptyCharacteristicFilterObjectAdvancedMode()
  );

  filterStore.characteristicFilterLogicalOperators.push(false); //There has to be a logical operator for each filter value (in advanced mode)
}

/**
 *
 * @param {number} index
 */
function removeLocationAddressFilter(index: number) {
  const filterStore = useFilterStore();

  filterStore.locationAddressFilters.splice(index, 1);
  filterStore.locationAddressFilterLogicalOperators.splice(index, 1);
}

function addLocationNameFilterInput() {
  const filterStore = useFilterStore();

  filterStore.locationNameFilters.push(getEmptyGenericFilterObject());
  filterStore.locationNameFilterLogicalOperators.push(false);
}

/**
 *
 * @param {number} index
 */
function removeLocationNameFilter(index: number) {
  const filterStore = useFilterStore();

  filterStore.locationNameFilters.splice(index, 1);
  filterStore.locationNameFilterLogicalOperators.splice(index, 1);
}

/**
 *
 * @param {number} index
 */
function removeCharacteristicFilter(index: number) {
  const filterStore = useFilterStore();

  const realIndex = getRealIndex(index);
  const charToDel = filterStore.characteristicFilters[realIndex];
  let pageReload = false;
  if (
    filterStore.characteristicFilters.length ===
    filterStore.characteristicFilterLogicalOperators.length
  ) {
    pageReload = true;
  }
  if (charToDel.charactType !== FilterCharactType.INTERVENTION) {
    filterStore.characteristicFilters.splice(realIndex, 1);
  } else {
    removeCharacteristicInterventionFilters(realIndex);
  }
  //If a filter value is removed, the corresponding logical operator has to be removed as well.
  if (pageReload) {
    filterStore.characteristicFilterLogicalOperators.splice(realIndex, 1);
    if (
      filterStore.characteristicFilters.length <
      filterStore.characteristicFilterLogicalOperators.length
    ) {
      //there are logical operators for interventions with multiple codes that need to be deleted as well
      const amountToDel =
        filterStore.characteristicFilterLogicalOperators.length -
        filterStore.characteristicFilters.length;
      for (let i = 0; i < amountToDel; i++) {
        filterStore.characteristicFilterLogicalOperators.splice(realIndex, 1);
      }
    }
  } else {
    filterStore.characteristicFilterLogicalOperators.splice(index, 1);
  }
}

function removeCharacteristicInterventionFilters(index: number) {
  const filterStore = useFilterStore();

  // Only FilterInfoObjectInputVal is expected, not VariantInfo.
  const displayValue = (
    filterStore.characteristicFilters[index]
      .inputVal as FilterInfoObjectInputVal
  ).display.toLowerCase();
  filterStore.characteristicFilters = filterStore.characteristicFilters.filter(
    (e) => {
      return (
        e.charactType !== FilterCharactType.INTERVENTION ||
        (e.inputVal as FilterInfoObjectInputVal).display.toLowerCase() !==
          displayValue
      );
    }
  );
  if (filterStore.characteristicFilters.length === 0) {
    //All characteristic filter inputs have been removed
    addEmptyCharacteristicFilterInput(); //Add empty filter input
  }
}

function getEmptyGenericFilterObject() {
  const objectTemplate = {
    inputVal: { code: '', display: '', system: '' },
    paramType: '',
  };
  return { ...objectTemplate };
}

function getEmptyLocationCityFilterObject() {
  const filterObj = getEmptyGenericFilterObject();
  filterObj.paramType = 'city';
  return filterObj;
}

function addLocationAddressFilterInput() {
  const filterStore = useFilterStore();

  filterStore.locationAddressFilters.push(
    getEmptyGenericFilterObject() as LocationFilterInfo
  );
  filterStore.locationAddressFilterLogicalOperators.push(false);
}

function initFilter() {
  const filterStore = useFilterStore();

  if (filterStore.locationAddressFilters.length === 0) {
    addLocationAddressFilterInput();
  }
  if (filterStore.locationNameFiltersSimpleMode.length === 0) {
    filterStore.locationNameFiltersSimpleMode.push(
      getEmptyGenericFilterObject()
    );
  }
  if (filterStore.locationNameFilters.length === 0) {
    addLocationNameFilterInput();
  }
  if (filterStore.characteristicFilters.length === 0) {
    addEmptyCharacteristicFilterInput();
  }

  if (
    filterStore.individualCharacteristicFilterArrays.pathwayFilters.length === 0
  ) {
    //Initialize new empty object to represent input
    const filterObj = getEmptyCharacteristicFilterObjectAdvancedMode();
    filterStore.individualCharacteristicFilterArrays.pathwayFilters.push(
      filterObj
    );
  }
}

/**
 * Replaces the characteristic filter at the given (UI) index with the given filter object.
 * If the new filter is a condition filter whose code is an ICD-10 parent code, the row
 * being edited is replaced by the whole ICD group (parent + all descendants) instead.
 * The group always uses the deterministic categoryId for the new root code
 * (see getIcdGroupCategoryId), so editing the root to a different parent code
 * replaces the old group instead of mixing its members together.
 * @param inp the new filter object
 * @param index UI index of the filter to replace
 */
function updateCharacteristicFilters(
  inp: CharacteristicFilterInfoObject,
  index: number
) {
  const filterStore = useFilterStore();
  index = getRealIndex(index);
  const vs =
    useFhirStore().valueSets.conditionValueSetWithSpecificAndGeneralInfoSeparated ??
    [];
  if (
    inp.charactType === FilterCharactType.CONDITION &&
    inp.inputVal &&
    'code' in inp.inputVal &&
    inp.inputVal.code &&
    inp.inputVal.code !== '' &&
    icdProcessor.isIcdParentCode(inp.inputVal.code, vs)
  ) {
    //Replace the row being edited with the whole ICD group (parent + all descendants)
    filterStore.characteristicFilters.splice(index, 1);
    if (index < filterStore.characteristicFilterLogicalOperators.length) {
      filterStore.characteristicFilterLogicalOperators.splice(index, 1);
    } else {
      //Legacy out-of-sync state (mirrors the pageReload logic in removeCharacteristicFilter)
      console.warn(
        'characteristicFilterLogicalOperators is out of sync with ' +
          'characteristicFilters; could not remove the logical operator at index ' +
          index
      );
    }
    //Only the inputVal is relevant for the group expansion (code/display/exclusionCriterion)
    addIcdConditionFiltersGroupAdvancedMode(
      {
        id: getIcdGroupCategoryId(inp.inputVal.code),
        filterObject: { inputVal: inp.inputVal },
      },
      index
    );
    return;
  }
  let inpObj;
  if (
    inp.inputVal === undefined ||
    ('code' in inp.inputVal && inp.inputVal.code === '')
  ) {
    inpObj = getEmptyCharacteristicFilterObjectAdvancedMode();
  } else {
    inpObj = { ...inp };
  }

  filterStore.characteristicFilters.splice(index, 1, inpObj); //TODO Warum ist hier am Ende ein leeres default filterobject?
}

function updateCharacteristicInterventionFilters(
  inp: CharacteristicFilterInfoObject,
  index: number,
  codesArr: Coding[]
) {
  const filterStore = useFilterStore();
  index = getRealIndex(index);
  //inp is of type CharacteristicFilterInfoObject
  let inpObj;
  const inpObjArr = new Array<CharacteristicFilterInfoObject>();
  if (
    inp.inputVal === undefined ||
    ('code' in inp.inputVal && inp.inputVal.code === '')
  ) {
    inpObj = getEmptyCharacteristicFilterObjectAdvancedMode();
    inpObjArr.push(inpObj);
  } else {
    if (
      (
        filterStore.characteristicFilters[index]
          .inputVal as FilterInfoObjectInputVal
      ).code !== ''
    ) {
      //change an existing filter tag
      updateSeveralCharacteristicInterventionFilters(inp, index, codesArr);
      return;
    } else {
      //add new filter tag (change an empty one)
      codesArr.forEach((e) => {
        const newInpObj = JSON.parse(JSON.stringify(inp));
        newInpObj.inputVal.code = e.code;
        inpObjArr.push(newInpObj);
      });
    }
  }
  filterStore.characteristicFilters.splice(index, 1, ...inpObjArr);
}

function updateSeveralCharacteristicInterventionFilters(
  inp: CharacteristicFilterInfoObject,
  index: number,
  codesArr: Coding[]
) {
  removeCharacteristicInterventionFilters(index);
  const filterStore = useFilterStore();

  if (
    filterStore.characteristicFilters.length <
    filterStore.characteristicFilterLogicalOperators.length
  ) {
    //there are logical operators for interventions with multiple codes that need to be deleted as well
    const amountToDel =
      filterStore.characteristicFilterLogicalOperators.length -
      filterStore.characteristicFilters.length;
    for (let i = 0; i < amountToDel; i++) {
      filterStore.characteristicFilterLogicalOperators.splice(index, 1);
    }
  }
  const inpObjArr = new Array<CharacteristicFilterInfoObject>();
  const logOpsArr = new Array<boolean>();
  codesArr.forEach((e) => {
    const newInpObj = JSON.parse(JSON.stringify(inp));
    newInpObj.inputVal.code = e.code;
    inpObjArr.push(newInpObj);
    logOpsArr.push(false);
  });
  filterStore.characteristicFilters.splice(index, 0, ...inpObjArr);
  filterStore.characteristicFilterLogicalOperators.splice(index, 0, false);
}

function updateCharactAdjuvantFilter(
  inp: CharacteristicFilterInfoObject,
  index: number
) {
  const filterStore = useFilterStore();
  index = getRealIndex(index);
  //inp is of type CharacteristicFilterInfoObject
  if (inp.valueBoolean === undefined || inp.valueBoolean.toString() === '') {
    const newEmptyInpObj = getEmptyCharacteristicFilterObjectAdvancedMode();
    filterStore.characteristicFilters.splice(index, 1, newEmptyInpObj);
  } else {
    filterStore.characteristicFilters.splice(index, 1, inp);
  }
}

function updateCharacteristicPathwayFilter(inputText: string, index: number) {
  const filterStore = useFilterStore();
  index = getRealIndex(index);
  let inpObj;
  if (inputText === undefined || inputText === null) {
    //Initialize new empty object to represent input and replace old filter with that
    inpObj = getEmptyCharacteristicFilterObjectAdvancedMode();
    filterStore.characteristicFilters.splice(index, 1, inpObj);
  } else {
    //Overwrite old values in existing filter object
    inpObj = filterStore.characteristicFilters[index];
    inpObj.inputVal.code = inputText;
    inpObj.inputVal.display = inputText;
  }
}

const resetFilter = () => {
  //Clear filter inputs
  useFilterStore().$reset(); //function provided by pinia
  //Restore filter menu to standard state
  initFilter();
  //Reload

  useGeneralFlagStore().filterResetDone(); //Mark reset as done
};

/**
 * @param inputObj object containing input event (originally delivered by v-select in FilterComponent) and an id. Structure: {id: String, event: Object}
 * @param index index after which to add the new input entry
 * @param diagnoses diagnosis object to add as filters type: {system?:string, code:string, display?:string}[]
 */
async function addConditionFiltersFromCategoryAdvancedMode(
  inputObj, //{ id: string, filterObject: CharacteristicFilterInfoObject, input: { code: string; display?: string | undefined }}
  index: number,
  diagnoses: Coding[]
) {
  index = getRealIndex(index);
  const fhirStore = useFhirStore();
  const icdCodes = diagnoses.map((diagnoseInfo) => diagnoseInfo.code);

  //Match extracted codes against entries in pre-processed version of the icd-10 VS where general and specific info have been separated out of display value
  const diagnosesWithCompletedInfo =
    valueSetHelper.getMatchingVsEntriesForGivenCodes(
      icdCodes,
      fhirStore.valueSets.conditionValueSetWithSpecificAndGeneralInfoSeparated
    );

  diagnosesWithCompletedInfo.forEach(
    (diagnosis) =>
      addConditionFilterWithGivenInputValAdvancedModeAtGivenIndex(
        diagnosis,
        inputObj.id,
        index + 1,
        true,
        inputObj.filterObject.inputVal?.exclusionCriterion
      ) //Id to identify filters added by a specific filter input and belonging to a specific category. Set flag to indicate automatic creation.
  );
  checkForObsoleteParentCategoryAndRemoveObsoleteFilters(); //Called here, because server query has to be awaited
}

function addConditionFilterWithGivenInputValAdvancedModeAtGivenIndex(
  inputVal: FilterInfoObjectInputVal, //{icdGeneral: string, icdSpecific: string, code: string, display: string}
  id: string,
  index: number,
  autoCreatedByCategorySelection: boolean,
  exclusionCriterion: boolean
) {
  const filterStore = useFilterStore();
  filterStore.characteristicFilters.splice(index, 0, {
    inputVal: { ...inputVal, exclusionCriterion },
    charactType: FilterCharactType.CONDITION,
    valueBoolean: undefined,
    categoryId: id,
    isParentCategoryFilterElement: false,
    autoCreatedByCategorySelection: autoCreatedByCategorySelection,
  });

  filterStore.characteristicFilterLogicalOperators.splice(index, 0, false); //Default value false leads to logical OR
}

// ---------------------------------------------------------------------------
// ICD-10 group handling
//
// Selecting an ICD-10 parent code (e.g. 'C24') as a diagnosis filter expands
// the selection client-side into the parent code plus all of its descendant
// codes ('C24.0', 'C24.1', ...), each stored as an individual condition
// filter. All group members share one categoryId so the group can be tracked
// and cleaned up as a whole (see isIcdGroupRootEntry and
// getObsoleteCharacteristicFilterInputs).
// ---------------------------------------------------------------------------

/**
 * Pattern matching the categoryId used for ICD-10 groups.
 * Formats: advanced mode '0|Diagnose|<code>' (deterministic, see getIcdGroupCategoryId),
 * simple mode 'Diagnose|<code>'. In both formats the last segment is the group's
 * root code. 'Diagnose' must be a full segment, so a categoryId like
 * 'DiagnoseX|...' does not match.
 */
const ICD_GROUP_CATEGORY_ID_PATTERN = /(^|\|)Diagnose\|/;

/**
 * Checks whether the given categoryId belongs to an ICD-10 group.
 * @param categoryId categoryId to check
 * @returns true iff the categoryId matches the ICD-group pattern
 */
const isIcdGroupCategoryId = (categoryId?: string): boolean => {
  return (
    typeof categoryId === 'string' &&
    ICD_GROUP_CATEGORY_ID_PATTERN.test(categoryId)
  );
};

/**
 * Returns the deterministic categoryId for an ICD-10 group in advanced mode.
 * The same root code always yields the same categoryId, so re-selecting or
 * re-adding a group replaces the existing one instead of creating duplicates
 * (e.g. two different rows selecting the same code yield one group).
 * The format has 3 segments ('0|Diagnose|<code>') so the advanced-mode tooltip
 * rendering (categoryId.split('|')[1] + ': ' + categoryId.split('|')[2]) shows
 * 'Diagnose: <code>'.
 * @param rootCode ICD-10 root code of the group, e.g. 'C00'
 * @returns the group's categoryId, e.g. '0|Diagnose|C00'
 */
const getIcdGroupCategoryId = (rootCode: string): string =>
  '0|Diagnose|' + rootCode;

/**
 * Returns the code of a characteristic filter's inputVal if it has one.
 * @param filter filter to inspect
 * @returns the code, or undefined if the inputVal is not a coding-like object
 */
const getFilterCode = (
  filter: CharacteristicFilterInfoObject
): string | undefined => {
  if (filter.inputVal && 'code' in filter.inputVal) {
    return filter.inputVal.code;
  }
  return undefined;
};

/**
 * Builds the inputVal of an ICD-10 group filter from a ValueSet entry.
 * The general/specific ICD info is taken from the entry itself if present,
 * otherwise it is extracted from the display value.
 * @param entry ValueSet entry of the group member
 * @param exclusionCriterion exclusion flag inherited from the parent's input, if any
 * @returns the inputVal for the group member's filter
 */
const buildIcdGroupFilterInputVal = (
  entry: IcdValueSetEntry,
  exclusionCriterion?: boolean
): FilterInfoObjectInputVal => {
  return {
    code: entry.code,
    display: entry.display,
    system: entry.system ?? '',
    icdSpecific:
      entry.icdSpecific ?? icdProcessor.extractIcdSpecific(entry.display),
    icdGeneral: entry.icdGeneral ?? icdProcessor.extractIcdGeneral(entry.display),
    exclusionCriterion,
  };
};

/**
 * Adds the given ICD-10 parent code and all of its descendants as individual
 * condition filters in simple mode. Each group member becomes its own removable
 * filter tag. Codes that are already present in the condition filter array are
 * skipped to avoid duplicates.
 * @param parentEntry ValueSet entry of the selected ICD-10 parent code
 */
function addIcdConditionFiltersGroupSimpleMode(
  parentEntry: IcdValueSetEntry
): void {
  const filterStore = useFilterStore();
  const vs =
    useFhirStore().valueSets.conditionValueSetWithSpecificAndGeneralInfoSeparated ??
    [];

  const children = icdProcessor.getIcdChildrenEntries(parentEntry.code, vs);
  const groupCategoryId = 'Diagnose|' + parentEntry.code;

  const existingCodes = new Set(
    filterStore.individualCharacteristicFilterArrays.conditionFilters
      .map((filter) => getFilterCode(filter))
      .filter((code): code is string => code !== undefined)
  );

  [parentEntry, ...children].forEach((entry) => {
    if (existingCodes.has(entry.code)) {
      //Code is already filtered on -> skip to avoid duplicates
      return;
    }
    filterStore.individualCharacteristicFilterArrays.conditionFilters.push({
      charactType: FilterCharactType.CONDITION,
      inputVal: buildIcdGroupFilterInputVal(entry),
      autoCreatedByCategorySelection: true,
      categoryId: groupCategoryId,
    });
    existingCodes.add(entry.code);
  });
}

/**
 * Removes all simple-mode condition filters belonging to the ICD-10 group of
 * the given root code (i.e. all entries whose categoryId is 'Diagnose|<rootCode>').
 * @param rootCode ICD-10 root code of the group, e.g. 'C24'
 * @returns true if at least one filter was removed, false otherwise
 */
function removeIcdConditionFiltersGroupSimpleMode(rootCode: string): boolean {
  const filterStore = useFilterStore();
  const groupCategoryId = 'Diagnose|' + rootCode;

  const conditionFilters =
    filterStore.individualCharacteristicFilterArrays.conditionFilters;
  const remainingFilters = conditionFilters.filter(
    (filter) => filter.categoryId !== groupCategoryId
  );

  filterStore.individualCharacteristicFilterArrays.conditionFilters =
    remainingFilters;
  return remainingFilters.length !== conditionFilters.length;
}

/**
 * Adds the given ICD-10 parent code and all of its descendants as individual
 * condition filters in advanced mode (flat list with parallel logical operators).
 * The parent is inserted first, followed by the children, all at the given store
 * index (the position is incremented after each insertion). The parallel
 * logical-operator array is kept in sync.
 * Any pre-existing group members with the same categoryId are removed before the
 * insertion, so re-adding a group replaces the existing group (no duplicates
 * possible).
 * @param inputObj object containing an id (used as the group's categoryId) and the filter object currently being edited
 * @param index store index at which to insert the group (already mapped via getRealIndex by the caller)
 */
function addIcdConditionFiltersGroupAdvancedMode(
  inputObj: {
    id: string;
    filterObject: { inputVal?: Partial<FilterInfoObjectInputVal> };
  },
  index: number
): void {
  const filterStore = useFilterStore();
  const vs =
    useFhirStore().valueSets.conditionValueSetWithSpecificAndGeneralInfoSeparated ??
    [];

  const parentCode =
    inputObj.filterObject?.inputVal?.code ?? inputObj.id.split('|').pop();
  if (!parentCode || parentCode === 'undefined') {
    //No usable parent code (e.g. the id fallback produced the literal 'undefined')
    return;
  }

  const children = icdProcessor.getIcdChildrenEntries(parentCode, vs);
  const parentEntry: IcdValueSetEntry =
    vs.find((entry) => entry.code === parentCode) ?? {
      code: parentCode,
      display: inputObj.filterObject?.inputVal?.display ?? parentCode,
    };
  const exclusionCriterion =
    inputObj.filterObject?.inputVal?.exclusionCriterion;

  //Remove all pre-existing members of a group with the same categoryId so that
  //re-adding a group replaces it instead of duplicating codes
  for (
    let i = filterStore.characteristicFilters.length - 1;
    i >= 0;
    i--
  ) {
    if (filterStore.characteristicFilters[i].categoryId === inputObj.id) {
      filterStore.characteristicFilters.splice(i, 1);
      if (i < filterStore.characteristicFilterLogicalOperators.length) {
        filterStore.characteristicFilterLogicalOperators.splice(i, 1);
      }
    }
  }

  [parentEntry, ...children].forEach((entry) => {
    filterStore.characteristicFilters.splice(index, 0, {
      charactType: FilterCharactType.CONDITION,
      inputVal: buildIcdGroupFilterInputVal(entry, exclusionCriterion),
      valueBoolean: undefined,
      categoryId: inputObj.id,
      isParentCategoryFilterElement: false,
      autoCreatedByCategorySelection: true,
    });
    //Keep the parallel logical-operator array in sync (default false = logical OR)
    filterStore.characteristicFilterLogicalOperators.splice(index, 0, false);
    index++;
  });
}

/**
 * Checks whether the given filter is the root (parent code) of its ICD-10 group.
 * A filter qualifies as a group root iff it is an auto-generated condition filter
 * with an ICD-group categoryId and its code equals the last segment of that
 * categoryId (the group's root code).
 * Note: this relies on the ICD-group categoryId format, whose last segment is
 * always the group's root code (see getIcdGroupCategoryId for advanced mode and
 * 'Diagnose|<code>' for simple mode).
 * @param filter filter to check
 * @returns true iff the filter is the root of its ICD-10 group
 */
function isIcdGroupRootEntry(filter: CharacteristicFilterInfoObject): boolean {
  if (filter.charactType !== FilterCharactType.CONDITION) {
    return false;
  }
  if (filter.autoCreatedByCategorySelection !== true) {
    return false;
  }
  if (!filter.categoryId || !isIcdGroupCategoryId(filter.categoryId)) {
    return false;
  }
  const rootCode = getFilterCode(filter);
  if (rootCode === undefined) {
    return false;
  }
  return rootCode === filter.categoryId.split('|').pop();
}

/**
 * Filter entries have a categoryId identifying which category they belong to (Tumorentiät or Tumorregion and the selected sub category like Milz, Auge...). This method checks if there are filter entries whose category
 * is no longer present. This happens when a (tumor entity or tumor region) category is changed or deleted.
 * Any entries found to be obsolete are then removed.
 * Uses autoCreatedByCategorySelection flag on filter objects to identify filters that have to be checked for present parent category filter input.
 */
function checkForObsoleteParentCategoryAndRemoveObsoleteFilters() {
  const filtersToRemove = getObsoleteCharacteristicFilterInputs();
  if (filtersToRemove.length > 0) {
    //Remove Entries whose category has not been found
    removeListOfCharacteristicFiltersFromFilterListInStore(filtersToRemove);
  }
}

/**
 * Searches array with characteristic filters for advanced mode in filterStore for entries that have been automatically created by selection of a filter category like 'Tumorentität'.
 * If the corresponding parent category filter input is no longer present in the filter list, the auto generated filter inputs for that category are added to the output array.
 * For ICD-10 group filters (categoryId matching the ICD-group pattern) the rule differs:
 * a group member is obsolete only if its group no longer contains a root (see isIcdGroupRootEntry).
 * This way, removing the group root orphans the remaining children, which are then removed here.
 */
function getObsoleteCharacteristicFilterInputs() {
  const filterStore = useFilterStore();

  const filtersToRemove = new Array<CharacteristicFilterInfoObject>();

  //Isolate regular filter inputs
  const autoGeneratedFilterInputs = filterStore.characteristicFilters
    .filter((f) => f.isParentCategoryFilterElement !== true)
    .filter((f) => f.autoCreatedByCategorySelection === true);

  const parentFilters = filterStore.characteristicFilters.filter(
    (f) => f.isParentCategoryFilterElement === true
  );

  autoGeneratedFilterInputs.forEach((filter) => {
    if (filter.categoryId && isIcdGroupCategoryId(filter.categoryId)) {
      //ICD-10 group filter: obsolete only if the group no longer contains its root,
      //i.e. a member whose code equals the last segment of the group's categoryId
      const rootCode = filter.categoryId.split('|').pop();
      const groupHasRoot = filterStore.characteristicFilters.some(
        (f) =>
          f.categoryId === filter.categoryId &&
          f.charactType === FilterCharactType.CONDITION &&
          getFilterCode(f) === rootCode
      );
      if (!groupHasRoot) {
        filtersToRemove.push(filter);
      }
      return;
    }

    const foundParentCategory = parentFilters //Isolate category selects
      .some((f) => f.categoryId === filter.categoryId); // Check if at least one category filter element has the same categoryId as the current filter

    if (!foundParentCategory) {
      //Parent category not found -> mark for removal
      filtersToRemove.push(filter);
    }
  });

  return filtersToRemove;
}

/**
 * Removes all automatically created condition filters belonging to the given category, e.g. Tumorregion (category) Herz (displayVal of tag).
 * @param category 'Tumorregion' or 'Tumorentität' or their english counterparts
 * @param filter either of type CharacteristicFilterInfoObject or CharacteristicFilterTag
 */
async function removeConditionFiltersByCategory(category: string, filter) {
  const filterStore = useFilterStore();
  //dependant on type of param tag
  const displayVal = filter.inputVal || filter.value;

  const codeEntries = await fhirDao.getDiagnosesForCategory(
    displayVal,
    category
  );

  const icdCodes = codeEntries.map((el) => el.code);
  const elementsToRemove = new Array<CharacteristicFilterInfoObject>();
  //Identify filter elements to be removed
  for (
    let i = 0;
    i <
    filterStore.individualCharacteristicFilterArrays.conditionFilters.length;
    i++
  ) {
    const currElem =
      filterStore.individualCharacteristicFilterArrays.conditionFilters[i];
    if (
      'code' in currElem.inputVal &&
      icdCodes.includes(currElem.inputVal.code) &&
      currElem.categoryId === filter.categoryId
    ) {
      elementsToRemove.push(currElem);
    }
  }

  //Now remove marked elements
  elementsToRemove.forEach((elem) =>
    filterStore.individualCharacteristicFilterArrays.conditionFilters.splice(
      filterStore.individualCharacteristicFilterArrays.conditionFilters.indexOf(
        elem
      ),
      1
    )
  );
}

/**
 * Removes filter elements in given array from target array in filterStore (for advanced mode)
 * @param filtersToRemove Array with filter objects to remove from array of characteristic filter in filterStore
 */
function removeListOfCharacteristicFiltersFromFilterListInStore(
  filtersToRemove
) {
  const filterStore = useFilterStore();

  //Remove obsolete filters
  for (let i = filtersToRemove.length - 1; i >= 0; i--) {
    //Find index of element to be removed
    //consider interventions with multiple codes
    const indexOfElementInArray = findIndexConsideringMultipleInterventionCodes(
      filtersToRemove[i]
    );

    if (indexOfElementInArray !== undefined) {
      //Remove only if index has been found
      removeCharacteristicFilter(indexOfElementInArray);
    }
  }
  if (filterStore.characteristicFilters.length === 0) {
    //All characteristic filter inputs have been removed
    addEmptyCharacteristicFilterInput(); //Add empty filter input
  }
}

/**
 * Finds the index of a characteristic filter object ignoring intervention filters with same display.
 * This is relevant for the corrensponding index of the logical operator in filterStore.characteristicFilterLogicalOperators
 * @param targetFilter the filter objects whose index is to be found
 */
function findIndexConsideringMultipleInterventionCodes(targetFilter) {
  const filterStore = useFilterStore();

  let currentDisplayValue: string | undefined;
  let index = 0;
  for (let i = 0; i < filterStore.characteristicFilters.length; i++) {
    const filter = filterStore.characteristicFilters[i];
    if (filter.charactType === FilterCharactType.INTERVENTION) {
      //Cast should be okay because of charactType check
      if (
        (filter.inputVal as FilterInfoObjectInputVal).display.toLowerCase() !==
        currentDisplayValue
      ) {
        currentDisplayValue = (
          filter.inputVal as FilterInfoObjectInputVal
        ).display.toLowerCase();
        index++;
      }
    } else {
      index++;
    }
    if (filter === targetFilter) {
      return index - 1;
    }
  }
}

/**
 * Searches array with characteristic filters for advanced mode in filterStore for entries that represent a filter category like 'Tumorentität' or 'Tumorregion'.
 * If none of the expected auto generated filter inputs for that category are present, the filter input for that category is added to the output array.
 */
function getObsoleteCategoryFilterInputs() {
  const filterStore = useFilterStore();

  const obsoleteCategoryFilterInputs =
    new Array<CharacteristicFilterInfoObject>();

  //Isolate category filter inputs
  const categoryFilterInputs = filterStore.characteristicFilters.filter(
    (f) => f.isParentCategoryFilterElement === true
  );

  categoryFilterInputs.forEach((categoryFilter) => {
    const foundAssociatedFilter = filterStore.characteristicFilters
      .filter((f) => !f.isParentCategoryFilterElement) //Isolate only actual filters without category selects
      .filter((f) => f.autoCreatedByCategorySelection === true) //only consider filter inputs that have been automatically created by category selection
      .some((f) => f.categoryId === categoryFilter.categoryId); // Check if at least one element has the same categoryId as the parent category filter

    if (!foundAssociatedFilter) {
      //No associated filter found -> single category input -> mark for removal
      obsoleteCategoryFilterInputs.push(categoryFilter);
    }
  });

  return obsoleteCategoryFilterInputs;
}

/**
 * Removes tumorentity or tumorregion filters if none of the filters automatically generated by their selection remain as active filters.
 * @returns true if filters have been removed. Otherwise false.
 */
function removeObsoleteCategoryFiltersAdvancedMode() {
  const categoryFilterElementsToRemove = getObsoleteCategoryFilterInputs();

  if (categoryFilterElementsToRemove.length > 0) {
    removeListOfCharacteristicFiltersFromFilterListInStore(
      categoryFilterElementsToRemove
    );
    return true;
  }
  return false;
}

/**
 * Sets flag for only showing genetically stratified trials in advanced mode to false.
 */
function removeGeneticallyStratifiedFlagAdvancedMode() {
  const filterStore = useFilterStore();

  filterStore.showOnlyGeneticallyStratifiedTrialsAdvancedMode = false;
}

/**
 * Sets flag for only showing genetically stratified trials in simple mode to false.
 */
function removeGeneticallyStratifiedFlagSimpleMode() {
  const filterStore = useFilterStore();

  filterStore.showOnlyGeneticallyStratifiedTrialsSimpleMode = false;
}

async function combinedCheckForObsoleteFilters() {
  const filterStore = useFilterStore();

  console.log('combinedCheckForObsoleteFilters');
  await removeObsoleteCategoryFiltersSimpleMode(
    filterStore.individualCharacteristicFilterArrays.tumorcategoryFilters,
    t('trialsList.tumorcategory')
  ); //Do this first as it is very likely that it removes all diagnoses for the other category filters.
  const entityCheck = removeObsoleteCategoryFiltersSimpleMode(
    filterStore.individualCharacteristicFilterArrays.tumorentityFilters,
    t('trialsList.tumorentity')
  );
  const regionCheck = removeObsoleteCategoryFiltersSimpleMode(
    filterStore.individualCharacteristicFilterArrays.tumorregionFilters,
    t('trialsList.tumorregion')
  );
  await Promise.all([entityCheck, regionCheck]);
}

/**
 * Looks for all diagnosis filters that have been auto generated by given parent category filter. Then changes thei exclude propterty to that given in newExcludeStatus.
 * @param {{ code: string; value: boolean; display: string }} newExcludeStatus
 * @param {CharacteristicFilterInfoObject} parentCategory
 */
const updateExcludeStatusOfGeneratedDiagnosisFiltersForCategory = function (
  newExcludeStatus: { code: string; value: boolean; display: string },
  parentCategory: CharacteristicFilterInfoObject
) {
  const filterStore = useFilterStore();

  filterStore.characteristicFilters
    .filter(
      (characteristicFilter) =>
        characteristicFilter.charactType === FilterCharactType.CONDITION
    )
    .filter(
      (characteristicFilter) =>
        characteristicFilter.autoCreatedByCategorySelection
    )
    .filter(
      (characteristicFilter) =>
        characteristicFilter.categoryId === parentCategory.categoryId
    )
    .forEach((characteristicFilter) => {
      // Cast should be fine because of earlier charactType check
      (
        characteristicFilter.inputVal as FilterInfoObjectInputVal
      ).exclusionCriterion = newExcludeStatus.code === 'exclude';
    });
};

/**
 * @param {number} index index of filterObject to change in filterStore
 * @param {Coding} newBiomarkerValue value to be used for biomarkerValueCode
 */
const updateBiomarkerValueAdvancedFilterMode = function (
  index: number,
  newBiomarkerValue: Coding
) {
  const filterStore = useFilterStore();

  index = getRealIndex(index);
  (
    filterStore.characteristicFilters[index]
      .inputVal as FilterInfoObjectInputVal
  ).biomarkerValueCoding = newBiomarkerValue;
};

const clearBiomarkerFiltersSimpleMode = function () {
  const filterStore = useFilterStore();

  filterStore.individualCharacteristicFilterArrays.biomarkerFilters.length = 0;
};

/**
 * Adds all given biomarker filters to filterStore in simple filter mode
 * @param {Array<CharacteristicFilterInfoObject>} biomarkerFiltersToAdd
 */
const addBiomarkerFilters = function (
  biomarkerFiltersToAdd: Array<CharacteristicFilterInfoObject>
) {
  const filterStore = useFilterStore();

  filterStore.individualCharacteristicFilterArrays.biomarkerFilters =
    filterStore.individualCharacteristicFilterArrays.biomarkerFilters.concat(
      biomarkerFiltersToAdd
    );
};

/**
 * Changes the exclude property of filter category in filterStore described by parentCategory parameter to that given in newExcludeStatus.
 * @param {*} newExcludeStatus
 * @param {*} parentCategory
 */
const updateExcludeStatusOfParentCategoryFilter = function (
  newExcludeStatus,
  parentCategory
) {
  const filterStore = useFilterStore();

  const matchingFilterInStore = filterStore.characteristicFilters.find(
    (filter) =>
      filter.isParentCategoryFilterElement &&
      filter.categoryId === parentCategory.categoryId
  );
  if (matchingFilterInStore) {
    (
      matchingFilterInStore.inputVal as FilterInfoObjectInputVal
    ).exclusionCriterion = newExcludeStatus.code === 'exclude';
  } else {
    console.warn({
      msg: 'Could not find given parent category filter in filterStore.',
      parentCategory,
    });
  }
};

/**
 * Resets characteristic filter at given index to default empty filter object.
 * @param {number} index
 */
const resetGivenCharacteristicFilterContent = function (index: number) {
  const filterStore = useFilterStore();

  index = getRealIndex(index);
  if (filterStore.characteristicFilters[index + 1]) {
    if (
      // Casts should be fine due to this check
      filterStore.characteristicFilters[index + 1].charactType ===
        FilterCharactType.INTERVENTION &&
      (
        filterStore.characteristicFilters[index]
          .inputVal as FilterInfoObjectInputVal
      ).display.toLowerCase() ===
        (
          filterStore.characteristicFilters[index + 1]
            .inputVal as FilterInfoObjectInputVal
        ).display.toLowerCase()
    ) {
      removeCharacteristicInterventionFilters(index);
    }
  }
  const newEmptyInpObj = getEmptyCharacteristicFilterInputObject();
  //Replace only inputValue of existing filter object
  filterStore.characteristicFilters.splice(index, 1, {
    ...filterStore.characteristicFilters[index],
    inputVal: newEmptyInpObj,
  });
};

/**
 * because interventions with same display and different codes are only shown once but are in store for every code,
 * the index given by the components isn't the true index of the element in the store becuase it does not account for the intervention display duplicates
 * This method returns the true index taking into account the duplicates in the store
 * @param {number} index
 * @returns true index
 */
const getRealIndex = function (index: number) {
  const filterStore = useFilterStore();

  const filterArr = filterStore.characteristicFilters;
  let filteredIndex = 0; //count of unique, non-duplicate entries encountered so far
  for (
    let filterArrIndex = 0;
    filterArrIndex < filterArr.length;
    filterArrIndex++
  ) {
    const currentObj = filterArr[filterArrIndex];
    if (currentObj.inputVal && typeof currentObj.inputVal === 'object') {
      let isDuplicate = false;
      //check if currentObj is a duplicate by comparing it with all previous objects
      for (
        let comparisonIndex = 0;
        comparisonIndex < filterArrIndex;
        comparisonIndex++
      ) {
        if (
          // Casts should be fine due to this check
          currentObj.charactType === FilterCharactType.INTERVENTION &&
          (currentObj.inputVal as FilterInfoObjectInputVal).display &&
          filterArr[comparisonIndex].inputVal &&
          (filterArr[comparisonIndex].inputVal as FilterInfoObjectInputVal)
            .display ===
            (currentObj.inputVal as FilterInfoObjectInputVal).display
        ) {
          isDuplicate = true;
          break;
        }
      }
      //if currentObj is not a duplicate, check if it matches the requested index
      if (!isDuplicate) {
        if (filteredIndex === index) {
          return filterArrIndex; // true index found in original store
        }
        if (
          filterArr[filterArrIndex + 1] === undefined ||
          (filterArr[filterArrIndex + 1] !== undefined &&
            filterArr[filterArrIndex + 1].charactType !== undefined &&
            filterArr[filterArrIndex + 1].charactType === 'unknown')
        ) {
          //if last element in list
          return filteredIndex;
        }
        filteredIndex++;
      }
    }
  }
  return index;
};

function isFilterInfoObjectInputVal(
  inputVal: FilterInfoObjectInputVal | VariantInfo | VariantInfo[]
) {
  return (
    inputVal &&
    typeof inputVal === 'object' &&
    'display' in inputVal &&
    typeof inputVal.display === 'string'
  );
}

/**
 * interventions with multiple codes for one display are oly shown once, but the hidden codes also have logical operators after a page reload, these need to be ignored for the UI
 * @returns the indices of logical operators to be shown on the UI (= the indices of logical operators not corresponding to hidden codes)
 */
const getIndexOfLogicalOperatorsToBeShown = function () {
  const filterStore = useFilterStore();

  if (
    filterStore.characteristicFilters.some(
      (filter) => filter.charactType === FilterCharactType.INTERVENTION
    )
  ) {
    //there are interventions therfore possible hidden codes
    if (
      filterStore.characteristicFilterLogicalOperators.length ===
      filterStore.characteristicFilters.length
    ) {
      //interventions without hidden codes or after page reload
      if (
        (() => {
          // Create an object that has all display values as keys and number of occurrences as values.
          const displayCounts: object =
            filterStore.characteristicFilters.reduce(
              (acc: object, e: CharacteristicFilterInfoObject) => {
                let display: string;
                if (isFilterInfoObjectInputVal(e.inputVal)) {
                  display = (
                    e.inputVal as FilterInfoObjectInputVal
                  ).display.toLowerCase();
                } else {
                  display = (e.inputVal as VariantInfo).title.toLowerCase(); //variant (protein change) filter has inputVal not of type FilterInfoObjectInputVal and therefore no inputVal.display
                }
                acc[display] = (acc[display] || 0) + 1;
                return acc;
              },
              {}
            );
          // Check if any display value occurs more than once
          return Object.values(displayCounts).some((count) => count > 1);
        })()
      ) {
        //after reload of page therefore hidden codes with logical operators to be ignored
        const indexOfWantedLogOps = new Array<number>();
        const lastInterventionIndexes = new Map();
        filterStore.characteristicFilters.forEach((element, index) => {
          if (element.charactType === FilterCharactType.INTERVENTION) {
            let display: string;
            if (isFilterInfoObjectInputVal(element.inputVal)) {
              // Cast is ok due to check
              display = (
                element.inputVal as FilterInfoObjectInputVal
              ).display.toLowerCase();
            } else {
              display = (element.inputVal as VariantInfo).title.toLowerCase(); //variant (protein change) filter has inputVal not of type FilterInfoObjectInputVal and therefore no inputVal.display
            }
            lastInterventionIndexes.set(display, index);
          } else {
            indexOfWantedLogOps.push(index);
          }
        });
        lastInterventionIndexes.forEach((lastIndex) => {
          indexOfWantedLogOps.push(lastIndex);
        });
        indexOfWantedLogOps.sort((a, b) => a - b);
        return indexOfWantedLogOps;
      } else {
        //interventions without hidden codes
        return filterStore.characteristicFilterLogicalOperators.map(
          (_, index) => index
        );
      }
    } else {
      //interventions with hidden codes before page reload therefore no logical operators to be ignored
      return filterStore.characteristicFilterLogicalOperators.map(
        (_, index) => index
      );
    }
  } else {
    //no intervention filter tags therefore no logical operators to be ignored
    return filterStore.characteristicFilterLogicalOperators.map(
      (_, index) => index
    );
  }
};

/**
 *
 * @param {CharacteristicFilterInfoObject} variantFilterToRemove is expected to contain variant info
 */
const removeVariantFilterSimpleMode = (
  variantFilterToRemove: CharacteristicFilterInfoObject
) => {
  if (!('accession' in variantFilterToRemove.inputVal)) {
    throw new Error(
      'Expected CharacteristicFilterInfoObject with VariantInfo object as input, but received something else.'
    );
  }

  useFilterStore().individualCharacteristicFilterArrays.proteinChangeFilters =
    useFilterStore().individualCharacteristicFilterArrays.proteinChangeFilters.filter(
      (variantFilter) =>
        !areVariantsEquivalent(
          variantFilterToRemove.inputVal as VariantInfo,
          variantFilter.inputVal as VariantInfo
        )
    );
};

/**
 * Resets position variables associated with perimeter search in filter store for advanced search
 */
const resetUsrPos = () => {
  const filterStore = useFilterStore();

  filterStore.usrPos.lat = null;
  filterStore.usrPos.lon = null;
  filterStore.usrPos.city = null;
};

/**
 * Resets position variables associated with perimeter search in filter store for simple search
 */
const resetUsrPosSimpleMode = () => {
  const filterStore = useFilterStore();
  filterStore.usrPosSimpleMode.lat = null;
  filterStore.usrPosSimpleMode.lon = null;
  filterStore.usrPosSimpleMode.city = null;
};

/**
 * Transforms a new filter into a filter object for the filter store and adds it to the filter array it belongs to.</br>
 * Previously added filters are CharacteristicFilterInfoObject objects, newly added filters are FilterInfoObjectInputVal objects (or VariantInfo, if a protein change filter is selected).
 * @param activeFiltersInCategory Can be multiple, if multiselect is active. The one at the end is the one most recently added.
 * @param charactType Type of characteristic, e.g. 'condition'
 * @param nameOfFilterInStore Name of filter array in filter store the new filter is to be added to
 */
const addSimpleModeIndividualCharacteristicFilter = (
  activeFiltersInCategory: (
    | VariantInfo
    | FilterInfoObjectInputVal
    | CharacteristicFilterInfoObject
  )[],
  charactType: FilterCharactType,
  nameOfFilterInStore: string
) => {
  const filterStore = useFilterStore();

  let filterObject: CharacteristicFilterInfoObject;
  if (
    charactType === FilterCharactType.TUMOR_CATEGORY ||
    charactType === FilterCharactType.TUMOR_ENTITY ||
    charactType === FilterCharactType.TUMOR_REGION
  ) {
    // Treat new filter as Coding
    const lastCodingElement = activeFiltersInCategory[
      activeFiltersInCategory.length - 1
    ] as FilterInfoObjectInputVal;
    // Check if Coding
    const isLastElementCoding = 'code' in lastCodingElement;
    if (!isLastElementCoding) {
      throw new Error(
        `Expected FilterInfoObjectInputVal object, but received ${JSON.stringify(
          lastCodingElement
        )}`
      );
    }

    let filterCharactTypeDescription: string;
    switch (charactType) {
      case FilterCharactType.TUMOR_CATEGORY:
        filterCharactTypeDescription = 'Tumorkategorie';
        break;
      case FilterCharactType.TUMOR_REGION:
        filterCharactTypeDescription = 'Tumorregion';
        break;
      case FilterCharactType.TUMOR_ENTITY:
        filterCharactTypeDescription = 'Tumorentität';
        break;
      default:
        filterCharactTypeDescription = '';
    }

    filterObject = {
      charactType: charactType,
      inputVal: {
        code: lastCodingElement.code ?? '',
        display: lastCodingElement.display ?? '',
        system: lastCodingElement.system ?? '',
      },
      isParentCategoryFilterElement: true,
      autoCreatedByCategorySelection: false,
      categoryId:
        Math.random() +
        '|' +
        filterCharactTypeDescription +
        '|' +
        lastCodingElement.code,
    };
  } else if (charactType == FilterCharactType.PROTEIN_CHANGE) {
    // Treat new filter as variant info
    const lastVariantElement = activeFiltersInCategory[
      activeFiltersInCategory.length - 1
    ] as VariantInfo;
    // Check if VariantInfo
    const isLastVariantElementVariantInfo = 'accession' in lastVariantElement;
    if (!isLastVariantElementVariantInfo) {
      throw new Error(
        `Expected VariantInfo object, but received ${JSON.stringify(
          lastVariantElement
        )}`
      );
    }

    filterObject = {
      charactType: charactType,
      inputVal: {
        title: lastVariantElement.title,
        accession: lastVariantElement.accession,
        gene_sort: lastVariantElement.gene_sort,
        protein_change: lastVariantElement.protein_change,
        exclusionCriterion: false,
      },
      autoCreatedByCategorySelection: false,
    };
  } else {
    // Treat new filter as Coding
    const lastCodingElement = activeFiltersInCategory[
      activeFiltersInCategory.length - 1
    ] as FilterInfoObjectInputVal;
    // Check if Coding
    const isLastElementCoding = 'code' in lastCodingElement;
    if (!isLastElementCoding) {
      throw new Error(
        `Expected FilterInfoObjectInputVal object, but received ${JSON.stringify(
          lastCodingElement
        )}`
      );
    }
    filterObject = {
      charactType: charactType,
      inputVal: {
        code: lastCodingElement.code ?? '',
        display: lastCodingElement.display ?? '',
        system: lastCodingElement.system ?? '',
        icdSpecific: lastCodingElement.icdSpecific ?? undefined,
        icdGeneral: lastCodingElement.icdGeneral ?? undefined,
      },
      autoCreatedByCategorySelection: false,
    };
  }
  filterStore.individualCharacteristicFilterArrays[nameOfFilterInStore].push(
    filterObject
  );
};

const removeSimpleModeIndividualCharacteristicFilter = (
  event,
  nameOfFilterOnStore
) => {
  const filterStore = useFilterStore();

  filterStore.individualCharacteristicFilterArrays[nameOfFilterOnStore] =
    filterStore.individualCharacteristicFilterArrays[
      nameOfFilterOnStore
    ].filter((e) => {
      return (
        (e.inputVal as FilterInfoObjectInputVal).display.toLowerCase() !==
        event.inputVal.display.toLowerCase()
      );
    });
};

export default {
  removeSimpleModeIndividualCharacteristicFilter,
  addSimpleModeIndividualCharacteristicFilter,
  resetUsrPos,
  resetUsrPosSimpleMode,
  updateParentCategoryList,
  removeObsoleteCategoryFiltersSimpleMode,
  addFiltersFromCategorySimpleMode,
  getEmptyCharacteristicFilterObjectAdvancedMode,
  addEmptyCharacteristicFilterInput,
  removeCharacteristicFilter,
  removeLocationAddressFilter,
  removeLocationNameFilter,
  addLocationNameFilterInput,
  getEmptyGenericFilterObject,
  addLocationAddressFilterInput,
  initFilter,
  updateCharacteristicFilters,
  updateCharacteristicInterventionFilters,
  updateCharactAdjuvantFilter,
  updateCharacteristicPathwayFilter,
  addConditionFiltersFromCategoryAdvancedMode,
  addIcdConditionFiltersGroupSimpleMode,
  addIcdConditionFiltersGroupAdvancedMode,
  removeIcdConditionFiltersGroupSimpleMode,
  isIcdGroupRootEntry,
  isIcdGroupCategoryId,
  checkForObsoleteParentCategoryAndRemoveObsoleteFilters,
  removeConditionFiltersByCategory,
  removeListOfCharacteristicFiltersFromFilterListInStore,
  removeObsoleteCategoryFiltersAdvancedMode,
  combinedCheckForObsoleteFilters,
  removeGeneticallyStratifiedFlagAdvancedMode,
  removeGeneticallyStratifiedFlagSimpleMode,
  updateExcludeStatusOfGeneratedDiagnosisFiltersForCategory,
  updateExcludeStatusOfParentCategoryFilter,
  resetGivenCharacteristicFilterContent,
  updateBiomarkerValueAdvancedFilterMode,
  clearBiomarkerFiltersSimpleMode,
  addBiomarkerFilters,
  getEmptyLocationCityFilterObject,
  removeVariantFilterSimpleMode,
  getIndexOfLogicalOperatorsToBeShown,
  readIndividualCharacteristicFiltersFromUrl,
  setIndividualCharacteristicFilterInStore,
  resetFilter,
};
